import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  EventBusService,
  LogService,
  environment,
  User,
  WindowService,
} from '@ups/xplat/core';
import { deepMerge, setHttpCacheIdentityScope } from '@ups/xplat/utils';
import {
  Company,
  getFullNameFromInfo,
  ILimitedEmployeeInfoAndContact,
  IODataResult,
  NewUser,
  UserProfile,
} from '@ups/xplat/api/dto';
import {
  IAddressChangeRequest,
  INonBillableDefaultJob,
  IVacationAndSickBalance,
} from '../models';
import { mapUserToEmployeeInfo } from './user-profile.mapper';
import {
  EmergencyContactModel,
  IEmployeeData,
  IEmployeeInfo,
  SecurityUser,
} from '@ups/xplat/api/dto';
import { Role } from '../models/role.interface';

const SECURITY_GET_LOGGED_IN_USER = 'api/user/userProfile';
const SPARTA_POST_ADDRESS_CHANGE = `/api/spartaemployee/addresschangerequest/`;
const SECURITY_GET_USER_ROLES = `/api/user/roles`;
const SECURITY_GET_USER_JOB_TITLES = `/api/user/jobTitles`;

const PUT_EMPLOYEE_PROFILE_BASIC = '/api/hrrm/employee/{:id}';
const POST_EMPLOYEE_PROFILE_BASICDETAILS = '/api/hrrm/employee/basic';
const SPARTA_UPDATE_EMPLOYEE = 'api/spartaemployee/update';
const POST_EMPLOYEE_PROFILE_EMPLOYMENTDETAILS = '/api/hrrm/employee/employment';
const POST_EMPLOYEE_PROFILE_POSITIONDETAILS = '/api/hrrm/employee/position';
const POST_EMPLOYEE_PROFILE_BENEFITDETAILS = '/api/hrrm/employee/benefits';
const POST_EMPLOYEE_PROFILE_IDENTIFICATIONDETAILS =
  '/api/hrrm/employee/identification';

const GET_COMPANIES = '/api/hqco/company';
const GET_LICENSE_TYPES = '/api/manpowers/licensetypes';
const GET_IDCARD_TYPES = '/api/IdCard/HrAppIdCardTypes';
// const GET_EMPLOYEE_POSITION_NAMES = '/api/data/employeepositions/names';
const CHECK_ADDRESS_CHANGE_REQUEST = (hrref: number) =>
  `api/spartaemployee/addresschangerequest/${hrref}`;

const SEND_RESET_EMAIL = '/api/resetPassword';
const GET_USER = (userId) => `/api/user/${userId}`;
const GET_USER_HRREF = (hrref) => `/api/user/hrref/${hrref}`;
/**
 * Email-keyed identity to UserId mapping. Entra (MSAL) sign-ins carry
 * neither an Auth0 `sub` nor the `http://security.universalplant.com/user-id`
 * claim, so the id has to be resolved from the UPN instead (#30531). The
 * server endpoint was built for exactly this and its own comment names the
 * "shared-lib resolver" that calls it; nothing on the client ever did.
 * Returns a bare GUID, 404 when no ACTIVE user matches.
 */
const GET_SECURITY_USER_ID_BY_EMAIL = (email: string) =>
  `/api/user/by-email/${encodeURIComponent(email)}`;
const UPDATE_EMPLOYEE_FOR_SPARTA_ONLY = '/api/spartaemployee/update';
const GET_EMPLOYEE_INFO_BY_SSN_COMPANYID =
  '/api/hrrm/employeeInfo/{:SSN}/{:compID}';
const GET_EMERGENCY_CONTACTS = '/api/hrrm/employee/emergencyContacts/{:hrRef}';
const SAVE_EMERGENCY_CONTACT_INFORMATION =
  '/api/hrrm/employee/emergencyContact';
const VP_GET_I9_ATTCH = (hrref) =>
  `/api/employeeportal/attachment/hrref/${hrref}/i9`;
const VP_GET_TWIC_ATTCH = (hrref) =>
  `/api/employeeportal/attachment/hrref/${hrref}/twic`;
const VP_GET_IDENTITY_ATTCH = (hrref) =>
  `/api/employeeportal/attachment/hrref/${hrref}/identity`;
const VP_GET_EMERGENCY_CONTACTS = (hrref) =>
  `/api/hrrm/employee/emergencyContacts/${hrref}`;
const VP_SAVE_EMERGENCY_CONTACT = `/api/hrrm/employee/emergencyContact`;
const SPARTA_GET_MULTICLASS = (hrref, prCos) =>
  `/api/spartaemployee/employeemulticlass/${hrref}?prCos=${prCos}`;

/**
 * Whether the signed-in caller has been resolved to an identified UPS user.
 * The key split is between the two terminal-unresolved arms: the server said
 * there is no user, versus we could not reach it to ask. A consumer that logs
 * an unresolved user out (the unidentifiedUserGuard divert to /not-linked) must
 * act ONLY on the first, or a transient 500 locks out a linked user (PR 3803 P2).
 *
 *  - `pending`      fetch not settled. Normal, every boot.
 *  - `identified`   settled, `myInfo.UserId` usable.
 *  - `unidentified` DEFINITIVE no-user answer (404 #29713, or 200 w/ falsy
 *                   userId). Genuinely not linked. Terminal.
 *  - `error`        transport failure (500 / network / timeout): link state
 *                   UNKNOWN, must NOT read as "not linked". Terminal; diverts
 *                   must treat it as "do not divert".
 */
export type ProfileResolution =
  | 'pending'
  | 'identified'
  | 'unidentified'
  | 'error';

/**
 * Thrown by report/query builders when the current user's profile has not
 * resolved to a usable `UserId` (#30201, AC-2). The three unresolved arms
 * (transport failure, the #29713 authenticated-but-unresolved 404, and a 200
 * carrying a falsy `userId`) all reduce to a falsy `myInfo.UserId` at emit
 * time. Interpolating that into `rp:SecurityID=${...}` produces the literal
 * string `undefined`, which the paginated report rejects with a raw SQL 8169
 * rather than opening scoped data. Callers catch this to attempt a fresh
 * profile fetch and, failing that, show the user a message naming the
 * unresolved profile as the cause instead of a raw failure or a blank report.
 */
export class UnresolvedUserProfileError extends Error {
  constructor(action = 'this action') {
    super(
      `Your user profile has not finished loading, so ${action} cannot be completed. Please reload the page, or sign out and back in, and try again.`
    );
    this.name = 'UnresolvedUserProfileError';
    // Restore the prototype chain so `instanceof` works after transpilation
    // to ES5 targets (extending built-ins otherwise breaks the check).
    Object.setPrototypeOf(this, UnresolvedUserProfileError.prototype);
  }
}
@Injectable({
  providedIn: 'root',
})
export class UserService {
  myInfo: IEmployeeInfo;
  http: HttpClient;
  vpClient: HttpClient;
  spartaClient: HttpClient;
  securityClient: HttpClient;
  softwareSecurityClient: HttpClient;
  usersCache = [];

  private readonly _profileResolution = signal<ProfileResolution>('pending');

  /**
   * Read-only view of {@link ProfileResolution} for consumers that must
   * distinguish "still resolving" from "resolved to nobody" (#30191).
   *
   * Deliberately a signal rather than an event: the `profileUpdated` event is
   * emitted ONLY on the success path, so it cannot report the two failure
   * arms below, and a consumer keyed on the event reads a permanent failure
   * as an eternal "still loading".
   */
  readonly profileResolution = this._profileResolution.asReadonly();

  private logService: LogService = inject(LogService);
  private eventBus: EventBusService = inject(EventBusService);
  private httpClientFactory: MyHttpClientFactory = inject(MyHttpClientFactory);
  private win = inject(WindowService);

  constructor() {
    this.vpClient = this.httpClientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.spartaClient = this.httpClientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.securityClient = this.httpClientFactory.createHttpClient(
      environment.urls.securityAPI,
      true,
      ResponseCasingEnum.PascalCase
    );
    this.softwareSecurityClient = this.httpClientFactory.createHttpClient(
      environment.urls.softwareSecurityAPI,
      true,
      ResponseCasingEnum.PascalCase
    );

    // #33947: web only, a mobile logout event is cancellable; frontend-http.md.
    this.eventBus
      .observe(this.eventBus.types.logout)
      .subscribe(() => !this.win.isMobile && setHttpCacheIdentityScope(null));
  }

  get fullName() {
    return getFullNameFromInfo(this.myInfo);
  }

  fetchMyInfo(): Observable<IEmployeeInfo | null> {
    console.log('[UserService] fetchMyInfo called');
    return this.softwareSecurityClient.get(SECURITY_GET_LOGGED_IN_USER).pipe(
      map((u: User) => {
        // A 200 with an empty body is a DEFINITIVE no-user answer from the
        // server: settled, genuinely not linked. (A 404 throws and is handled
        // in catchError below; a 500/network failure must NOT reach here.)
        if (!u) {
          this._profileResolution.set('unidentified');
          return null;
        }
        const myInfo = mapUserToEmployeeInfo(u);
        this.myInfo = myInfo;

        // #33947: fill only; on mobile the token `sub` already set it (wins).
        setHttpCacheIdentityScope(u.auth0UserId, true);

        if (!this.win.isMobile) {
          this.saveProfileToLocalStorage(u);
        }

        // Gate on a USABLE id, not on the request succeeding. A 200 carrying a
        // falsy userId assigns myInfo and fires the event above, so anything
        // keyed on the response or on the event reads this cell as resolved
        // while every identity-scoped consumer still sees nothing.
        this._profileResolution.set(u.userId ? 'identified' : 'unidentified');

        this.eventBus.emit(this.eventBus.types.profileUpdated, myInfo);
        return this.myInfo;
      }),
      catchError((err: HttpErrorResponse) => {
        console.log('error:', err);
        // 404 = definitive "no linked user" (#29713), genuinely not linked.
        // Anything else (500 / timeout / network status 0) is a transport
        // failure: link state UNKNOWN, must NOT read as "not linked", or a flaky
        // API diverts a linked user to /not-linked (PR 3803 P2 finding).
        this._profileResolution.set(
          err?.status === 404 ? 'unidentified' : 'error'
        );
        return of(null);
      })
    );
  }

  saveProfileToLocalStorage(userProfile: User) {
    localStorage.setItem(
      'profile',
      JSON.stringify({
        hrRef: userProfile.hrRef,
        lastName: userProfile.lastName,
        firstName: userProfile.firstName,
        email: userProfile.email,
        workPhone: userProfile.cellPhone,
        proxyHRRef: userProfile.proxyHRRef,
        auth0UserId: userProfile.auth0UserId,
      })
    );
  }

  getRoles() {
    return this.softwareSecurityClient
      .get<Array<Role>>(SECURITY_GET_USER_ROLES)
      .pipe(
        catchError((err) => {
          this.logService.debug('error:', err);
          return of(null);
        })
      );
  }

  getCompanies(userCompanies = false) {
    let url = GET_COMPANIES;
    if (userCompanies === true) {
      url += '?userCompanies=true';
    } else {
      url += '?userCompanies=false';
    }

    return this.vpClient.get<Company[]>(url);
  }

  updateEmployeeProfileBasic(employeeProfileBasic: unknown, id: string) {
    return this.vpClient.put(
      PUT_EMPLOYEE_PROFILE_BASIC.split('{:id}').join(id),
      employeeProfileBasic
    );
  }

  /* eslint-disable */
  updateEmployeeProfileBasicDetails(
    data: IEmployeeData
  ): Observable<IEmployeeData> {
    const body = deepMerge(data, {
      UPSEmail: data.udUPSEmail,
      MailingAddress: {
        Street1: data.MailingAddress?.Line1,
        Street2: data.MailingAddress?.Line2,
        PostCode: data.MailingAddress?.Zip,
      },
    });
    return forkJoin({
      vp: this.vpClient.post<IEmployeeData>(
        POST_EMPLOYEE_PROFILE_BASICDETAILS,
        body
      ),
      sparta: this.spartaClient.put(SPARTA_UPDATE_EMPLOYEE, data),
    }).pipe(map(() => body));
  }
  /* eslint-enable */

  getEmployeeEmergencyContacts(hrRef: string) {
    return this.vpClient.get(
      GET_EMERGENCY_CONTACTS.split('{:hrRef}').join(hrRef)
    );
  }

  updateEmployeeEmergencyContact(emergencyContact: EmergencyContactModel) {
    return this.vpClient.post(
      SAVE_EMERGENCY_CONTACT_INFORMATION,
      emergencyContact
    );
  }

  /* eslint-disable */
  getEmployeeInfoUsingSSNAndCompanyID(SSN: string, compID: string) {
    const url = GET_EMPLOYEE_INFO_BY_SSN_COMPANYID.split('{:SSN}')
      .join(SSN)
      .split('{:compID}')
      .join(compID);

    return this.vpClient.get(url);
  }
  /* eslint-enable */

  updateEmployeeProfileForSpartaOnly(data: unknown) {
    return this.spartaClient.put(UPDATE_EMPLOYEE_FOR_SPARTA_ONLY, data);
  }

  updateEmployeeProfileEmploymentDetails(data: unknown) {
    return this.vpClient.post(POST_EMPLOYEE_PROFILE_EMPLOYMENTDETAILS, data);
  }

  updateEmployeeProfilePositionDetails(data: unknown) {
    return this.vpClient.post(POST_EMPLOYEE_PROFILE_POSITIONDETAILS, data);
  }

  updateEmployeeProfileBenefitDetails(data: unknown) {
    return this.vpClient.post(POST_EMPLOYEE_PROFILE_BENEFITDETAILS, data);
  }

  updateEmployeeProfileIdentificationDetails(data: unknown) {
    return this.vpClient.post(
      POST_EMPLOYEE_PROFILE_IDENTIFICATIONDETAILS,
      data
    );
  }

  // getEmployeePositionNames() {
  //   return this.packetClient
  //     .get(GET_EMPLOYEE_POSITION_NAMES);
  // }

  fetchLicenseTypes(): Observable<unknown> {
    return this.spartaClient.get(GET_LICENSE_TYPES);
  }

  fetchIdCardTypes(): Observable<Record<string, string>> {
    return this.vpClient.get<Record<string, string>>(GET_IDCARD_TYPES);
  }

  sendPasswordResetEmail(email: string) {
    return this.securityClient.put(SEND_RESET_EMAIL, { email });
  }

  getUserFromSecurity(userID: string): Observable<SecurityUser> {
    return this.securityClient.get<SecurityUser>(GET_USER(userID));
  }

  /**
   * Resolve the Software Security UserId for an email address (#30531).
   * The client half of an endpoint that already existed for Entra sign-ins,
   * which carry no security-user-id claim.
   *
   * Degrades to null rather than throwing: a 404 here is the ordinary
   * "this UPN has no active security row" answer, not a fault, and the one
   * caller uses the id only to populate a state slot. Erroring would turn a
   * benign unlinked-account case into a broken boot.
   */
  getSecurityUserIdByEmail(email: string): Observable<string | null> {
    if (!email) return of(null);
    return this.securityClient
      .get(GET_SECURITY_USER_ID_BY_EMAIL(email), { responseType: 'text' })
      .pipe(
        map((id) => {
          // The endpoint returns a bare GUID. A JSON-quoted body is
          // unwrapped so callers never store a string with quotes in it.
          const value = String(id ?? '')
            .replace(/^"|"$/g, '')
            .trim();
          return value.length > 0 ? value : null;
        }),
        catchError(() => of(null))
      );
  }

  getUserFromSecurityByHrRef(hrRef: string | number) {
    return this.securityClient.get<UserProfile[]>(GET_USER_HRREF(hrRef));
  }

  /* eslint-disable */
  checkAddressChange(HRRef: number) {
    return this.spartaClient.get<IAddressChangeRequest>(
      CHECK_ADDRESS_CHANGE_REQUEST(HRRef)
    );
  }
  /* eslint-enable */

  fetchMyAttachments() {
    return this.fetchAttachments(this.myInfo.Data.HRRef, false);
  }

  /* eslint-disable */
  fetchAttachments(HRRef: number, includeI9: boolean = true) {
    if (includeI9) {
      return forkJoin({
        i9: this.vpClient.get<any[]>(VP_GET_I9_ATTCH(HRRef)),
        twic: this.vpClient.get<any[]>(VP_GET_TWIC_ATTCH(HRRef)),
        identity: this.vpClient.get<any[]>(VP_GET_IDENTITY_ATTCH(HRRef)),
      });
    }
    return forkJoin({
      twic: this.vpClient.get<any[]>(VP_GET_TWIC_ATTCH(HRRef)),
      identity: this.vpClient.get<any[]>(VP_GET_IDENTITY_ATTCH(HRRef)),
    });
  }

  fetchEmergencyContacts(HRRef: number) {
    return this.vpClient.get(VP_GET_EMERGENCY_CONTACTS(HRRef));
  }

  saveEmergencyContact(contact: unknown) {
    return this.vpClient.post(VP_SAVE_EMERGENCY_CONTACT, contact);
  }

  fetchEmployeeMulitclass(HRRef: number) {
    return this.spartaClient.get<Array<unknown>>(
      SPARTA_GET_MULTICLASS(HRRef, 'all')
    );
  }
  /* eslint-enable */

  submitAddressChangeRequest(val: IAddressChangeRequest) {
    return this.spartaClient.post<IAddressChangeRequest>(
      SPARTA_POST_ADDRESS_CHANGE,
      val
    );
  }

  fetchVacationAndSickInfo(hrRef: number): Observable<IVacationAndSickBalance> {
    const url = '/api/hrrm/employee/vacationandsick/hrref/{:hrRef}'
      .split('{:hrRef}')
      .join(hrRef.toString());
    return this.vpClient.get(url) as Observable<IVacationAndSickBalance>;
  }

  /** Employee's non-billable default job (bPREH.udNBJobNum) for the locked Individual Time Entry panel. */
  fetchNonBillableDefaultJob(
    hrRef: number
  ): Observable<INonBillableDefaultJob> {
    const url = `/api/hrrm/employee/nbdefaultjob/hrref/${hrRef}`;
    return this.vpClient.get(url) as Observable<INonBillableDefaultJob>;
  }

  fetchLimitedEmployeeInfoAndContactById(
    employeeId: string
  ): Observable<ILimitedEmployeeInfoAndContact> {
    const url = `/api/spartaemployee/getlimitedemployeeinfoandcontactbyid/${employeeId}`;
    return this.spartaClient.get<ILimitedEmployeeInfoAndContact>(url);
  }

  fetchUserJobTitles() {
    return this.softwareSecurityClient.get<string[]>(
      SECURITY_GET_USER_JOB_TITLES
    );
  }

  queryUsers(query: string) {
    return this.softwareSecurityClient.get<IODataResult<UserProfile>>(
      '/api/users?$count=true&' + query
    );
  }

  queryUsersScoped(query: string) {
    return this.softwareSecurityClient.get<IODataResult<UserProfile>>(
      '/api/usersScoped?$count=true&' + query
    );
  }

  saveUserV4(user: NewUser, skipAuth0OnCreate: boolean = false) {
    return this.softwareSecurityClient.put<string>(
      `/api/user/v4?skipAuth0OnCreate=${skipAuth0OnCreate}`,
      user
    );
  }
}
